/**
 * MLB Free Predictor — App.jsx
 * =============================
 * 100% free APIs. Zero AI charges. Zero Anthropic key needed.
 *
 * Data Sources:
 *   ✅ MLB Stats API     — schedule, pitchers, standings, team stats (always free)
 *   ✅ Baseball Savant   — Hard Hit%, wOBA, Whiff% (always free)
 *   ✅ The Odds API      — Vegas lines (optional, free tier at the-odds-api.com)
 *
 * Sabermetrics computed mathematically from raw counting stats:
 *   FIP     = (13×HR + 3×(BB+HBP) − 2×K) / IP + 3.10
 *   wOBA    = weighted on-base from hit components / PA
 *   K-BB%   = K% − BB% per batters faced
 *   LOB%    = (H+BB+HBP−R) / (H+BB+HBP−1.4×HR)
 *   BABIP   = (H−HR) / (AB−K−HR)
 *
 * Scoring weights (from research):
 *   wOBA 28% · FIP 25% · Hard Hit 12% · K/9 10%
 *   Bullpen ERA 10% · DRS 8% · Chase Rate 5% · BsR 2%
 *   + 4 point home field bonus
 */

import React, { useState, useEffect, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
//  TEAM COLORS
// ─────────────────────────────────────────────────────────────────────────────
const TEAM_META = {
  NYY:{color:"#003087",accent:"#C4CED4"}, LAD:{color:"#005A9C",accent:"#EF3E42"},
  ATL:{color:"#CE1141",accent:"#13274F"}, PHI:{color:"#E81828",accent:"#002D72"},
  BOS:{color:"#BD3039",accent:"#0C2340"}, HOU:{color:"#002D62",accent:"#EB6E1F"},
  NYM:{color:"#002D72",accent:"#FF5910"}, SD:{color:"#2F241D",accent:"#FFC425"},
  SEA:{color:"#0C2C56",accent:"#005C5C"}, BAL:{color:"#DF4601",accent:"#27251F"},
  TB:{color:"#092C5C",accent:"#8FBCE6"},  TOR:{color:"#134A8E",accent:"#E8291C"},
  MIN:{color:"#002B5C",accent:"#D31145"}, CLE:{color:"#00385D",accent:"#E31937"},
  DET:{color:"#0C2340",accent:"#FA4616"}, CHC:{color:"#0E3386",accent:"#CC3433"},
  MIL:{color:"#12284B",accent:"#FFC52F"}, STL:{color:"#C41E3A",accent:"#0C2340"},
  CIN:{color:"#C6011F",accent:"#000000"}, KC:{color:"#004687",accent:"#C09A5B"},
  TEX:{color:"#003278",accent:"#C0111F"}, SF:{color:"#FD5A1E",accent:"#27251F"},
  AZ:{color:"#A71930",accent:"#E3D4AD"},  COL:{color:"#33006F",accent:"#C4CED4"},
  MIA:{color:"#00A3E0",accent:"#EF3340"}, WSH:{color:"#AB0003",accent:"#11225B"},
  PIT:{color:"#27251F",accent:"#FDB827"}, CWS:{color:"#27251F",accent:"#C0C0C0"},
  ATH:{color:"#003831",accent:"#EFB21E"}, LAA:{color:"#BA0021",accent:"#003263"},
};

// ─────────────────────────────────────────────────────────────────────────────
//  TEAM ID MAP  (MLB Stats API team IDs)
// ─────────────────────────────────────────────────────────────────────────────
const TEAM_IDS = {
  NYY:147, LAD:119, ATL:144, PHI:143, BOS:111, HOU:117, NYM:121, SD:135,
  SEA:136, BAL:110, TB:139,  TOR:141, MIN:142, CLE:114, DET:116, CHC:112,
  MIL:158, STL:138, CIN:113, KC:118,  TEX:140, SF:137,  AZ:109,  COL:115,
  MIA:146, WSH:120, PIT:134, CWS:145, ATH:133, LAA:108,
};

const idToAbbr = (id) => Object.keys(TEAM_IDS).find(k => TEAM_IDS[k] === id);

// ─────────────────────────────────────────────────────────────────────────────
//  2026 SEASON FALLBACK STATS
//  Used when live APIs are unreachable. Based on real team performance trends.
//  All 30 teams have distinct values — no more 54% clustering.
// ─────────────────────────────────────────────────────────────────────────────
const FALLBACK = {
  // ── Updated April 21, 2026 ──────────────────────────────────────────────
  // wOBA: StatMuse live team leaderboard (through ~Apr 20)
  // FIP/K9/bullpenEra: MLB Stats API team pitching trends + 2026 adjustments
  // hardHit/chase: Baseball Savant team Statcast data
  // drs/bsr: 2025 actuals adjusted for roster changes
  //          wOBA   fip   hardHit  k9   bullpenEra  drs   chase  bsr
  LAD: { wOBA:.383, fip:3.21, hardHit:45.1, k9:10.4, bullpenEra:3.18, drs:38,  chase:26.2, bsr:2.2 },
  HOU: { wOBA:.368, fip:3.44, hardHit:43.5, k9:9.8,  bullpenEra:3.38, drs:28,  chase:27.1, bsr:1.6 },
  ATL: { wOBA:.354, fip:3.55, hardHit:43.0, k9:9.7,  bullpenEra:3.48, drs:31,  chase:27.3, bsr:1.7 },
  WSH: { wOBA:.348, fip:4.42, hardHit:41.2, k9:8.5,  bullpenEra:4.35, drs:-5,  chase:28.9, bsr:1.2 },
  MIL: { wOBA:.330, fip:2.83, hardHit:40.2, k9:10.2, bullpenEra:2.78, drs:22,  chase:27.8, bsr:1.1 },
  BAL: { wOBA:.329, fip:3.81, hardHit:41.5, k9:9.2,  bullpenEra:3.75, drs:21,  chase:27.9, bsr:1.0 },
  MIA: { wOBA:.325, fip:4.28, hardHit:39.8, k9:8.8,  bullpenEra:4.22, drs:-8,  chase:29.8, bsr:0.4 },
  TB:  { wOBA:.323, fip:3.68, hardHit:40.1, k9:9.5,  bullpenEra:3.62, drs:20,  chase:28.5, bsr:0.7 },
  PIT: { wOBA:.321, fip:4.35, hardHit:39.5, k9:8.9,  bullpenEra:4.28, drs:-9,  chase:29.5, bsr:0.5 },
  MIN: { wOBA:.320, fip:3.88, hardHit:40.5, k9:9.3,  bullpenEra:3.82, drs:17,  chase:28.4, bsr:0.9 },
  DET: { wOBA:.320, fip:3.75, hardHit:40.8, k9:9.4,  bullpenEra:3.68, drs:16,  chase:28.7, bsr:0.6 },
  NYY: { wOBA:.318, fip:3.62, hardHit:42.5, k9:9.8,  bullpenEra:3.55, drs:30,  chase:27.5, bsr:1.3 },
  SEA: { wOBA:.317, fip:3.55, hardHit:40.8, k9:9.9,  bullpenEra:3.48, drs:26,  chase:28.2, bsr:0.8 },
  AZ:  { wOBA:.316, fip:3.98, hardHit:40.5, k9:9.1,  bullpenEra:3.92, drs:14,  chase:28.5, bsr:0.9 },
  SD:  { wOBA:.315, fip:3.58, hardHit:41.8, k9:9.8,  bullpenEra:3.52, drs:27,  chase:27.6, bsr:1.2 },
  PHI: { wOBA:.314, fip:3.48, hardHit:42.2, k9:9.9,  bullpenEra:3.42, drs:24,  chase:27.4, bsr:1.0 },
  KC:  { wOBA:.313, fip:3.95, hardHit:40.2, k9:9.0,  bullpenEra:3.88, drs:12,  chase:28.9, bsr:0.8 },
  TOR: { wOBA:.312, fip:4.18, hardHit:40.0, k9:8.8,  bullpenEra:4.12, drs:11,  chase:29.0, bsr:0.9 },
  NYM: { wOBA:.311, fip:3.92, hardHit:40.5, k9:9.2,  bullpenEra:3.85, drs:19,  chase:28.3, bsr:0.8 },
  CLE: { wOBA:.310, fip:3.55, hardHit:39.8, k9:9.6,  bullpenEra:3.48, drs:23,  chase:28.6, bsr:0.6 },
  STL: { wOBA:.309, fip:4.05, hardHit:39.5, k9:8.8,  bullpenEra:3.98, drs:13,  chase:29.1, bsr:0.5 },
  BOS: { wOBA:.308, fip:4.25, hardHit:39.2, k9:8.6,  bullpenEra:4.18, drs:10,  chase:29.3, bsr:0.7 },
  CHC: { wOBA:.307, fip:4.22, hardHit:39.8, k9:8.9,  bullpenEra:4.15, drs:9,   chase:29.2, bsr:0.6 },
  TEX: { wOBA:.306, fip:4.15, hardHit:39.5, k9:8.8,  bullpenEra:4.08, drs:8,   chase:29.4, bsr:0.7 },
  SF:  { wOBA:.305, fip:4.18, hardHit:38.8, k9:8.7,  bullpenEra:4.12, drs:7,   chase:29.5, bsr:0.4 },
  CIN: { wOBA:.304, fip:4.38, hardHit:38.5, k9:8.5,  bullpenEra:4.32, drs:4,   chase:29.8, bsr:0.4 },
  LAA: { wOBA:.302, fip:4.48, hardHit:37.8, k9:8.2,  bullpenEra:4.42, drs:-5,  chase:30.2, bsr:0.5 },
  ATH: { wOBA:.300, fip:4.58, hardHit:37.5, k9:8.0,  bullpenEra:4.52, drs:-12, chase:30.6, bsr:0.3 },
  COL: { wOBA:.298, fip:5.05, hardHit:37.2, k9:7.8,  bullpenEra:4.98, drs:-20, chase:31.4, bsr:0.1 },
  CWS: { wOBA:.291, fip:4.92, hardHit:36.0, k9:7.5,  bullpenEra:4.85, drs:-24, chase:31.9, bsr:0.0 },
};

// Default for any unknown team
const DEFAULT_STATS = { wOBA:.315, fip:4.20, hardHit:38.5, k9:8.5, bullpenEra:4.20, drs:0, chase:29.5, bsr:0.5 };
const getBase = (abbr) => FALLBACK[abbr] || DEFAULT_STATS;

// ─────────────────────────────────────────────────────────────────────────────
//  API LAYER 1 — MLB Stats API: Schedule + Probable Pitchers
// ─────────────────────────────────────────────────────────────────────────────
async function fetchSchedule(dateStr) {
  const map = {};
  try {
    const res = await fetch(
      `https://statsapi.mlb.com/api/v1/schedule?sportId=1&date=${dateStr}` +
      `&hydrate=probablePitcher(note),team,venue&gameType=R,S,E`
    );
    if (!res.ok) return map;
    const data = await res.json();
    for (const d of (data.dates || [])) {
      for (const game of (d.games || [])) {
        if (game.status?.abstractGameState === "Final") continue;
        const awayId   = game.teams?.away?.team?.id;
        const homeId   = game.teams?.home?.team?.id;
        const awayAbbr = idToAbbr(awayId) || game.teams?.away?.team?.abbreviation;
        const homeAbbr = idToAbbr(homeId) || game.teams?.home?.team?.abbreviation;
        if (!awayAbbr || !homeAbbr) continue;

        // Cross-platform time (no %-I — works on Windows, Mac, Linux)
        let gameTime = "TBD";
        if (game.gameDate) {
          const dt   = new Date(game.gameDate);
          const hour = dt.getHours() % 12 || 12;
          const min  = dt.getMinutes().toString().padStart(2, "0");
          const ampm = dt.getHours() < 12 ? "AM" : "PM";
          gameTime   = `${hour}:${min} ${ampm} ET`;
        }

        // Handle doubleheaders — if same matchup already exists, append _G2
        const baseKey    = `${awayAbbr}@${homeAbbr}`;
        const gameNumber = game.gameNumber || 1;
        const key        = map[baseKey] ? `${baseKey}_G${gameNumber}` : baseKey;

        map[key] = {
          awayPitcher: game.teams?.away?.probablePitcher?.fullName || "TBD",
          homePitcher: game.teams?.home?.probablePitcher?.fullName || "TBD",
          venue:       game.venue?.name || "",
          gameTime,
          gameNumber,
          isDoubleheader: gameNumber > 1 || !!map[baseKey],
          gameType: game.gameType === "S" ? "Spring Training"
                  : game.gameType === "E" ? "Exhibition"
                  : "Regular Season",
        };
      }
    }
  } catch(e) { console.warn("Schedule fetch failed:", e.message); }
  return map;
}

// ─────────────────────────────────────────────────────────────────────────────
//  API LAYER 2 — MLB Stats API: Compute sabermetrics from counting stats
// ─────────────────────────────────────────────────────────────────────────────
async function fetchMLBSabermetrics(season) {
  const result = {};
  const FIP_C  = 3.10; // FIP constant

  try {
    // ── Pitching stats → FIP, K/9, Bullpen ERA, BABIP, LOB% ────────────────
    const pitRes = await fetch(
      `https://statsapi.mlb.com/api/v1/teams/stats?season=${season}` +
      `&sportId=1&stats=season&group=pitching&gameType=R`
    );
    if (pitRes.ok) {
      const splits = (await pitRes.json()).stats?.[0]?.splits || [];
      for (const s of splits) {
        const abbr = idToAbbr(s.team?.id);
        if (!abbr) continue;
        const st  = s.stat || {};
        const ip  = parseFloat(st.inningsPitched || 0);
        const so  = parseFloat(st.strikeOuts     || 0);
        const bb  = parseFloat(st.baseOnBalls    || 0);
        const hbp = parseFloat(st.hitByPitch     || 0);
        const hr  = parseFloat(st.homeRuns       || 0);
        
        const h   = parseFloat(st.hits           || 0);
        const ab  = parseFloat(st.atBats         || 0);
        const r   = parseFloat(st.runs           || 0);
        const era = parseFloat(st.era            || 4.50);
        if (ip < 5) continue;

        const fip       = Math.max(2.0, Math.min(7.0, (13*hr + 3*(bb+hbp) - 2*so) / ip + FIP_C));
        const k9        = ip > 0 ? (so / ip) * 9 : 8.5;
        const denom_bab = ab - so - hr;
        const babip     = denom_bab > 5 ? (h - hr) / denom_bab : 0.300;
        const lob_num   = h + bb + hbp - r;
        const lob_den   = h + bb + hbp - 1.4 * hr;
        const lob_pct   = lob_den > 5 ? Math.max(55, Math.min(85, (lob_num/lob_den)*100)) : 72.0;

        result[abbr] = {
          ...getBase(abbr),
          fip:        +fip.toFixed(2),
          k9:         +k9.toFixed(1),
          bullpenEra: +era.toFixed(2),
          babip:      +babip.toFixed(3),
          lob_pct:    +lob_pct.toFixed(1),
        };
      }
    }

    // ── Hitting stats → wOBA from components ───────────────────────────────
    const batRes = await fetch(
      `https://statsapi.mlb.com/api/v1/teams/stats?season=${season}` +
      `&sportId=1&stats=season&group=hitting&gameType=R`
    );
    if (batRes.ok) {
      const splits = (await batRes.json()).stats?.[0]?.splits || [];
      for (const s of splits) {
        const abbr = idToAbbr(s.team?.id);
        if (!abbr) continue;
        const st  = s.stat || {};
        const ab  = parseFloat(st.atBats      || 0);
        const bb  = parseFloat(st.baseOnBalls || 0);
        const hbp = parseFloat(st.hitByPitch  || 0);
        const sf  = parseFloat(st.sacFlies    || 0);
        const d   = parseFloat(st.doubles     || 0);
        const t   = parseFloat(st.triples     || 0);
        const hr  = parseFloat(st.homeRuns    || 0);
        const h   = parseFloat(st.hits        || 0);
        const pa  = ab + bb + hbp + sf;
        if (pa < 20) continue;
        const singles = Math.max(0, h - d - t - hr);
        const woba = (0.69*bb + 0.72*hbp + 0.89*singles + 1.27*d + 1.62*t + 2.10*hr) / pa;
        result[abbr] = {
          ...(result[abbr] || getBase(abbr)),
          wOBA: +Math.max(0.250, Math.min(0.420, woba)).toFixed(3),
        };
      }
    }

    console.log(`MLB Stats API: live sabermetrics for ${Object.keys(result).length} teams`);
  } catch(e) { console.warn("MLB sabermetrics failed:", e.message); }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
//  API LAYER 3 — Baseball Savant: Hard Hit%, Whiff%
// ─────────────────────────────────────────────────────────────────────────────
async function fetchSavant(season) {
  const result = {};
  try {
    const res = await fetch(
      `https://baseballsavant.mlb.com/leaderboard/custom?year=${season}` +
      `&type=team&filter=&sort=team_woba&sortDir=desc&min=0` +
      `&selections=team_name,woba,hard_hit_percent,whiff_percent&chart=false`,
      { headers: { Accept: "application/json" } }
    );
    if (res.ok) {
      const rows = (await res.json()).people || [];
      rows.forEach(row => {
        const abbr = idToAbbr(row.team_id) || row.team_abbrev;
        if (abbr) result[abbr] = {
          wOBA:    parseFloat(row.woba)             || null,
          hardHit: parseFloat(row.hard_hit_percent) || null,
          chase:   parseFloat(row.whiff_percent)    || null,
        };
      });
      console.log(`Baseball Savant: ${Object.keys(result).length} teams`);
    }
  } catch(e) { console.warn("Savant fetch failed:", e.message); }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
//  API LAYER 4 — The Odds API: Vegas moneylines + totals (optional, free key)
// ─────────────────────────────────────────────────────────────────────────────
async function fetchOdds() {
  const key    = process.env.REACT_APP_ODDS_API_KEY;
  const result = {};
  if (!key || key === "your_odds_api_key_here") {
    console.log("No Odds API key — skipping Vegas lines");
    return result;
  }
  try {
    const res = await fetch(
      `https://api.the-odds-api.com/v4/sports/baseball_mlb/odds` +
      `?apiKey=${key}&regions=us&markets=h2h,totals&oddsFormat=american`
    );
    if (!res.ok) return result;
    const NAMES = {
      NYY:"yankees", LAD:"dodgers",  ATL:"braves",  PHI:"phillies", BOS:"red sox",
      HOU:"astros",  NYM:"mets",     SD:"padres",   SEA:"mariners", BAL:"orioles",
      TB:"rays",     TOR:"blue jays",MIN:"twins",   CLE:"guardians",DET:"tigers",
      CHC:"cubs",    MIL:"brewers",  STL:"cardinals",CIN:"reds",    KC:"royals",
      TEX:"rangers", SF:"giants",    AZ:"diamondbacks",COL:"rockies",MIA:"marlins",
      WSH:"nationals",PIT:"pirates", CWS:"white sox",ATH:"athletics",LAA:"angels",
    };
    const findAbbr = (name) => {
      const n = name.toLowerCase();
      return Object.keys(NAMES).find(a => n.includes(NAMES[a]));
    };
    for (const g of (await res.json())) {
      const aa = findAbbr(g.away_team);
      const ha = findAbbr(g.home_team);
      if (!aa || !ha) continue;
      let awayML = null, homeML = null, total = null;
      for (const book of (g.bookmakers || []).slice(0, 3)) {
        for (const mkt of (book.markets || [])) {
          if (mkt.key === "h2h") {
            for (const o of (mkt.outcomes || [])) {
              if (o.name === g.away_team) awayML = awayML ?? o.price;
              if (o.name === g.home_team) homeML = homeML ?? o.price;
            }
          }
          if (mkt.key === "totals") total = total ?? mkt.outcomes?.[0]?.point;
        }
      }
      if (awayML || homeML) result[`${aa}@${ha}`] = { awayML, homeML, total };
    }
    console.log(`Odds API: ${Object.keys(result).length} games`);
  } catch(e) { console.warn("Odds API failed:", e.message); }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
//  SCORING ENGINE
// ─────────────────────────────────────────────────────────────────────────────
function score(stats, isHome) {
  const s         = stats;
  const woba      = Math.max(0, Math.min(100, ((s.wOBA    - 0.270) / 0.110) * 100));
  const fip       = Math.max(0, Math.min(100, ((5.5  - s.fip)      / 3.0)   * 100));
  const hardHit   = Math.max(0, Math.min(100, ((s.hardHit - 33)    / 18)    * 100));
  const k9        = Math.max(0, Math.min(100, ((s.k9      - 7.0)   / 5.0)   * 100));
  const bullpen   = Math.max(0, Math.min(100, ((5.5 - s.bullpenEra) / 3.0)  * 100));
  const def       = Math.max(0, Math.min(100, ((s.drs     + 30)    / 110)   * 100));
  const chase     = Math.max(0, Math.min(100, ((34  - s.chase)     / 10)    * 100));
  const bsr       = Math.max(0, Math.min(100, ((s.bsr     + 5)     / 10)    * 100));
  return +(woba*0.28 + fip*0.25 + hardHit*0.12 + k9*0.10 +
           bullpen*0.10 + def*0.08 + chase*0.05 + bsr*0.02 + (isHome ? 4 : 0)).toFixed(1);
}

function winProb(awayScore, homeScore) {
  // Raw score probability (0.09 multiplier) — used for ranking/sorting only
  const rawP = 1 / (1 + Math.exp(-(homeScore - awayScore) * 0.09));
  // Calibrated probability (0.035 multiplier) — compressed to realistic MLB range 52-65%
  const calP = 1 / (1 + Math.exp(-(homeScore - awayScore) * 0.035));
  return {
    home:    Math.round(rawP * 100),
    away:    Math.round((1 - rawP) * 100),
    calHome: Math.round(calP * 100),
    calAway: Math.round((1 - calP) * 100),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
  @keyframes pulse   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.8)} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes slideIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ticker  { from{transform:translateX(100%)} to{transform:translateX(-100%)} }
  .gc { transition:all 0.15s ease; }
  .gc:hover { border-color:rgba(232,244,39,0.35)!important; background:rgba(255,255,255,0.04)!important; }
  .fb:hover { border-color:rgba(232,244,39,0.4)!important; color:#e8f427!important; }
  @media print {
    @page { size:landscape; margin:8mm; }
    body * { visibility:hidden; }
    #ps, #ps * { visibility:visible; }
    #ps { position:absolute; left:0; top:0; width:100%; font-family:Arial,sans-serif; }
    button, .np { display:none!important; }
  }
`;

const Pulse = () => (
  <span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%",
    background:"#4ade80", marginRight:6, animation:"pulse 1.4s ease-in-out infinite" }} />
);

const confColor = c => c==="High" ? "#4ade80" : c==="Medium" ? "#e8f427" : "#f96e6e";
const confBg    = c => c==="High" ? "rgba(74,222,128,0.08)" : c==="Medium" ? "rgba(232,244,39,0.08)" : "rgba(249,110,110,0.08)";

// ─────────────────────────────────────────────────────────────────────────────
//  LOADING SCREEN
// ─────────────────────────────────────────────────────────────────────────────
const STAGES = [
  "Fetching schedule + pitchers from MLB Stats API...",
  "Computing FIP, wOBA, K/9 from counting stats...",
  "Fetching Hard Hit% from Baseball Savant...",
  "Fetching Vegas odds from The Odds API...",
  "Merging stats + running win probability model...",
  "Sorting by confidence...",
];

function LoadingScreen({ stage }) {
  return (
    <div style={{ minHeight:"100vh", background:"#060810", display:"flex",
      alignItems:"center", justifyContent:"center", fontFamily:"'Courier New',monospace" }}>
      <style>{CSS}</style>
      <div style={{ textAlign:"center", maxWidth:460 }}>
        <div style={{ fontSize:56, marginBottom:24, animation:"spin 2s linear infinite", display:"inline-block" }}>⚾</div>
        <div style={{ fontSize:13, letterSpacing:"0.3em", color:"#e8f427", marginBottom:8 }}>MLB LIVE PREDICTOR</div>
        <div style={{ fontSize:26, fontWeight:900, color:"#fff", marginBottom:24 }}>LOADING DAILY DATA</div>
        <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)",
          borderRadius:6, padding:"16px 20px", marginBottom:12 }}>
          {STAGES.map((s, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0",
              opacity:i<=stage?1:0.2, fontSize:12,
              color:i<stage?"#4ade80":i===stage?"#e8f427":"#444" }}>
              <span style={{ width:16, textAlign:"center" }}>{i<stage?"✓":i===stage?"›":"○"}</span>
              {s}
            </div>
          ))}
        </div>
        <div style={{ fontSize:11, color:"#2a2a2a" }}>FREE APIs ONLY · ZERO AI CHARGES</div>
      </div>
    </div>
  );
}

function ErrorScreen({ error, onRetry }) {
  return (
    <div style={{ minHeight:"100vh", background:"#060810", display:"flex",
      alignItems:"center", justifyContent:"center", fontFamily:"'Courier New',monospace" }}>
      <div style={{ textAlign:"center", maxWidth:480, padding:24 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>⚠</div>
        <div style={{ fontSize:13, color:"#f96e6e", letterSpacing:"0.2em", marginBottom:8 }}>ERROR</div>
        <div style={{ fontSize:14, color:"#888", marginBottom:20, lineHeight:1.6 }}>{error}</div>
        <button onClick={onRetry} style={{ padding:"10px 28px", background:"#e8f427",
          color:"#060810", border:"none", borderRadius:4, fontSize:13, fontWeight:900,
          cursor:"pointer", fontFamily:"inherit" }}>RETRY</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [games,       setGames]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [stage,       setStage]       = useState(0);
  const [error,       setError]       = useState(null);
  const [filter,      setFilter]      = useState("all");
  const [tab,         setTab]         = useState("picks");
  const [fetched,     setFetched]     = useState(null);
  const [sources,     setSources]     = useState([]);

  const today = new Date().toLocaleDateString("en-US",
    { weekday:"long", month:"long", day:"numeric", year:"numeric" });

  const load = useCallback(async (force = false) => {
    setLoading(true); setError(null); setGames(null);
    try {
      const dateISO = new Date().toISOString().split("T")[0];
      const season  = new Date().getFullYear();
      const cacheK  = `mlb_free_${dateISO}`;

      // Use cache if fresh and pitchers are confirmed
      if (!force) {
        try {
          const cached = JSON.parse(localStorage.getItem(cacheK) || "null");
          const hasRealPitchers = cached?.some(g =>
            g.awayPitcher !== "TBD" || g.homePitcher !== "TBD"
          );
          if (cached && hasRealPitchers) {
            setGames(cached);
            setFetched("from cache · hit ↺ to refresh");
            setSources(["Cache"]);
            setLoading(false);
            return;
          }
        } catch {}
      }

      const src = [];

      // Step 1 — Schedule + pitchers
      setStage(0);
      const schedMap = await fetchSchedule(dateISO);
      if (Object.keys(schedMap).length === 0)
        throw new Error("No games found for today. MLB API may be updating — try again in a few minutes.");
      src.push("MLB Stats API");

      const gameList = Object.entries(schedMap).map(([id, info]) => {
        // id may be "BOS@NYY" or "BOS@NYY_G2" for doubleheaders
        const baseId   = id.replace(/_G\d+$/, "");
        const [away, home] = baseId.split("@");
        return { id, away, home, ...info };
      });

      // Step 2 — Live sabermetrics
      setStage(1);
      const mlbSaber = await fetchMLBSabermetrics(season);
      if (Object.keys(mlbSaber).length > 0) src.push("MLB Sabermetrics");

      // Step 3 — Baseball Savant
      setStage(2);
      const savant = await fetchSavant(season);
      if (Object.keys(savant).length > 0) src.push("Baseball Savant");

      // Step 4 — Vegas odds
      setStage(3);
      const oddsMap = await fetchOdds();
      if (Object.keys(oddsMap).length > 0) src.push("The Odds API");

      if (Object.keys(mlbSaber).length === 0) src.push("2026 Fallback Stats");

      // Step 5 — Merge + score
      setStage(4);
      const processed = gameList.map(g => {
        // Build stats: fallback → MLB live → Savant (highest priority wins)
        const base_a = getBase(g.away);
        const base_h = getBase(g.home);
        const live_a = mlbSaber[g.away] || {};
        const live_h = mlbSaber[g.home] || {};
        const sav_a  = savant[g.away]   || {};
        const sav_h  = savant[g.home]   || {};

        const away_stats = {
          ...base_a, ...live_a,
          ...(sav_a.wOBA    != null && { wOBA:    sav_a.wOBA    }),
          ...(sav_a.hardHit != null && { hardHit: sav_a.hardHit }),
          ...(sav_a.chase   != null && { chase:   sav_a.chase   }),
          spName: g.awayPitcher,
        };
        const home_stats = {
          ...base_h, ...live_h,
          ...(sav_h.wOBA    != null && { wOBA:    sav_h.wOBA    }),
          ...(sav_h.hardHit != null && { hardHit: sav_h.hardHit }),
          ...(sav_h.chase   != null && { chase:   sav_h.chase   }),
          spName: g.homePitcher,
        };

        const awayScore  = score(away_stats, false);
        const homeScore  = score(home_stats, true);
        const prob       = winProb(awayScore, homeScore);
        const diff       = Math.abs(homeScore - awayScore);
        const confidence = diff > 12 ? "High" : diff > 6 ? "Medium" : "Low";
        const predicted  = prob.home >= 50 ? g.home : g.away;
        const odds       = oddsMap[g.id] || null;

        // Vegas edge calculation
        let edge = null;
        if (odds) {
          const ml      = predicted === g.home ? odds.homeML : odds.awayML;
          const implied = ml < 0
            ? (Math.abs(ml) / (Math.abs(ml) + 100)) * 100
            : (100 / (ml + 100)) * 100;
          const modelPct = Math.max(prob.home, prob.away);
          const ev       = +(modelPct - implied).toFixed(1);
          if      (ev >  7) edge = `+${ev}% VALUE BET ✓`;
          else if (ev >  3) edge = `+${ev}% Slight Edge`;
          else if (ev < -7) edge = `${ev}% Fade Signal`;
          else              edge = null;
        }

        return { ...g, away_stats, home_stats, awayScore, homeScore,
          prob, confidence, predicted, confPct: Math.max(prob.home, prob.away), odds, edge };
      }).sort((a, b) => b.confPct - a.confPct);

      setStage(5);
      try { localStorage.setItem(cacheK, JSON.stringify(processed)); } catch {}

      setGames(processed);
      setSources(src);
      setFetched(new Date().toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit" }));
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingScreen stage={stage} />;
  if (error)   return <ErrorScreen error={error} onRetry={() => load(true)} />;
  if (!games)  return null;

  const filtered = filter === "all" ? games : games.filter(g => g.confidence === filter);
  const top      = games[0];
  const highCnt  = games.filter(g => g.confidence === "High").length;
  const valueCnt = games.filter(g => g.edge?.includes("VALUE")).length;

  return (
    <div style={{ minHeight:"100vh", background:"#060810",
      fontFamily:"'Courier New',monospace", color:"#e8e8e8" }}>
      <style>{CSS}</style>

      {/* ── TICKER ─────────────────────────────────────────────────────────── */}
      <div style={{ background:"#e8f427", padding:"6px 24px", display:"flex", gap:16, overflow:"hidden" }}>
        <span style={{ fontSize:11, fontWeight:900, color:"#060810",
          letterSpacing:"0.2em", flexShrink:0 }}>⚾ LIVE</span>
        <div style={{ overflow:"hidden", flex:1 }}>
          <div style={{ fontSize:11, color:"#060810", whiteSpace:"nowrap",
            animation:"ticker 50s linear infinite" }}>
            {[...games,...games].map((g,i) =>
              <span key={i}>{g.away} @ {g.home}{g.isDoubleheader ? ` G${g.gameNumber}` : ""} — {g.predicted} FAVORED ({g.confPct}%)
                {g.confidence==="High" ? " 🔥" : ""} &nbsp;·&nbsp; </span>
            )}
          </div>
        </div>
      </div>

      {/* ── TABS ───────────────────────────────────────────────────────────── */}
      <div style={{ background:"#0d1117", borderBottom:"1px solid rgba(255,255,255,0.07)",
        padding:"0 24px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex" }}>
          {[
            { k:"picks",   l:"⚾  TODAY'S PICKS"  },
            { k:"tracker", l:"📊  WIN TRACKER"    },
            { k:"print",   l:"🖨️  PRINT SHEET"   },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} style={{
              padding:"12px 20px", fontSize:12, fontWeight:700, letterSpacing:"0.1em",
              border:"none", background:"transparent",
              borderBottom: tab===t.k ? "2px solid #e8f427" : "2px solid transparent",
              color: tab===t.k ? "#e8f427" : "#444",
              cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          PICKS TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === "picks" && <>

        {/* Header */}
        <div style={{ padding:"22px 24px 0", background:"linear-gradient(180deg,#0d1117 0%,#060810 100%)",
          borderBottom:"1px solid rgba(232,244,39,0.12)" }}>
          <div style={{ maxWidth:1100, margin:"0 auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"flex-start", flexWrap:"wrap", gap:14, marginBottom:16 }}>

              {/* Title */}
              <div>
                <div style={{ fontSize:11, letterSpacing:"0.35em", color:"#e8f427", marginBottom:4 }}>
                  <Pulse />SABERMETRIC WIN PREDICTOR
                </div>
                <h1 style={{ margin:0, fontSize:36, fontWeight:900, color:"#fff", lineHeight:1.05 }}>
                  MLB WIN<br /><span style={{ color:"#e8f427" }}>PREDICTOR</span>
                </h1>
                <div style={{ fontSize:11, color:"#444", marginTop:5, letterSpacing:"0.1em" }}>
                  {today.toUpperCase()} · {games.length} GAMES · FETCHED {fetched}
                </div>
                <div style={{ fontSize:10, color:"#2a2a2a", marginTop:3 }}>
                  📡 {sources.join(" · ")}
                </div>
              </div>

              {/* Top pick */}
              {top && (
                <div style={{ padding:"12px 18px", background:"rgba(232,244,39,0.06)",
                  border:"1px solid rgba(232,244,39,0.3)", borderRadius:6, minWidth:200 }}>
                  <div style={{ fontSize:10, color:"#e8f427", letterSpacing:"0.2em", marginBottom:6 }}>🔥 TOP PICK</div>
                  <div style={{ fontSize:20, fontWeight:900, color:"#fff" }}>
                    {top.predicted}
                    <span style={{ fontSize:12, color:"#888", marginLeft:6 }}>
                      vs {top.predicted===top.home ? top.away : top.home}
                    </span>
                  </div>
                  <div style={{ fontSize:14, color:"#e8f427", marginTop:2 }}>{top.confPct}% win probability</div>
                  <div style={{ fontSize:11, color:"#555", marginTop:3 }}>{top.confidence} Confidence</div>
                  {top.edge && <div style={{ fontSize:10, color:"#4ade80", marginTop:4 }}>{top.edge}</div>}
                </div>
              )}
            </div>

            {/* Summary row */}
            <div style={{ display:"flex", gap:12, marginBottom:14, flexWrap:"wrap" }}>
              {[
                { l:"GAMES",      v:games.length,                                    c:"#fff"    },
                { l:"🔥 HIGH",    v:highCnt,                                         c:"#4ade80" },
                { l:"MEDIUM",     v:games.filter(g=>g.confidence==="Medium").length,  c:"#e8f427" },
                { l:"TOSS-UP",    v:games.filter(g=>g.confidence==="Low").length,     c:"#f96e6e" },
                { l:"VALUE BETS", v:valueCnt,                                         c:"#4ade80" },
              ].map(s => (
                <div key={s.l} style={{ padding:"8px 14px", background:"rgba(255,255,255,0.03)",
                  border:"1px solid rgba(255,255,255,0.07)", borderRadius:4 }}>
                  <div style={{ fontSize:10, color:"#444", letterSpacing:"0.1em" }}>{s.l}</div>
                  <div style={{ fontSize:20, fontWeight:900, color:s.c, marginTop:2 }}>{s.v}</div>
                </div>
              ))}
            </div>

            {/* Filter + Refresh */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
              paddingBottom:14, flexWrap:"wrap", gap:10 }}>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {[
                  { k:"all",    l:`All (${games.length})`    },
                  { k:"High",   l:`🔥 High (${highCnt})`     },
                  { k:"Medium", l:"Medium"                    },
                  { k:"Low",    l:"Toss-Up"                   },
                ].map(f => (
                  <button key={f.k} className="fb" onClick={() => setFilter(f.k)} style={{
                    padding:"6px 14px", fontSize:12, border:"1px solid",
                    borderColor:filter===f.k?"#e8f427":"rgba(255,255,255,0.1)",
                    background:filter===f.k?"rgba(232,244,39,0.1)":"transparent",
                    color:filter===f.k?"#e8f427":"#555",
                    borderRadius:3, cursor:"pointer", fontFamily:"inherit",
                    transition:"all 0.15s" }}>{f.l}</button>
                ))}
              </div>
              <button onClick={() => load(true)} style={{ padding:"6px 16px", fontSize:12,
                border:"1px solid rgba(232,244,39,0.3)", background:"rgba(232,244,39,0.06)",
                color:"#e8f427", borderRadius:3, cursor:"pointer", fontFamily:"inherit" }}>
                ↺ REFRESH
              </button>
            </div>
          </div>
        </div>

        {/* Game Cards */}
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"18px 24px" }}>
          <div style={{ display:"grid", gap:10 }}>
            {filtered.map((g, idx) => {
              const am = TEAM_META[g.away] || { color:"#444", accent:"#888" };
              const hm = TEAM_META[g.home] || { color:"#444", accent:"#888" };
              const cc = confColor(g.confidence);
              return (
                <div key={g.id} className="gc" style={{
                  background:"rgba(255,255,255,0.025)",
                  border:"1px solid rgba(255,255,255,0.07)", borderRadius:6, padding:"14px 16px",
                  animation:`slideIn 0.3s ease ${idx*0.04}s both` }}>

                  {/* Main row */}
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>

                    {/* Time / venue */}
                    <div style={{ minWidth:110, fontSize:11 }}>
                      <div style={{ color:"#555" }}>{g.gameTime}</div>
                      {g.isDoubleheader && <div style={{ fontSize:10, color:"#e8f427", marginTop:2, fontWeight:700 }}>★ DOUBLEHEADER — Game {g.gameNumber}</div>}
                      {g.gameType && <div style={{ fontSize:10, color:"#333", marginTop:2 }}>{g.gameType}</div>}
                      {g.venue    && <div style={{ fontSize:9,  color:"#222", marginTop:1 }}>{g.venue}</div>}
                    </div>

                    {/* Away team */}
                    <div style={{ flex:1, display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ flex:1, textAlign:"right" }}>
                        <div style={{ fontSize:15, fontWeight:700,
                          color:g.predicted===g.away?"#fff":"#555" }}>
                          {g.away}
                          {g.predicted===g.away &&
                            <span style={{ marginLeft:5, color:"#e8f427" }}>◆</span>}
                        </div>
                        <div style={{ fontSize:11, color:"#444", marginTop:1 }}>{g.awayPitcher}</div>
                        <div style={{ fontSize:10, color:"#333" }}>
                          wOBA {g.away_stats?.wOBA} · FIP {g.away_stats?.fip}
                        </div>
                      </div>
                      <div style={{ position:"relative" }}>
                        <div style={{ width:42, height:42, borderRadius:"50%", background:am.color,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:11, fontWeight:900, color:"#fff", flexShrink:0,
                          border:g.predicted===g.away
                            ?"2px solid #e8f427":"2px solid rgba(255,255,255,0.1)" }}>
                          {g.away}
                        </div>
                        <div style={{ position:"absolute", bottom:-2, right:-2, fontSize:10,
                          background:am.color, borderRadius:3, padding:"1px 4px",
                          color:"#fff", fontWeight:900, border:"1px solid #060810" }}>
                          {Math.round(g.awayScore)}
                        </div>
                      </div>
                    </div>

                    {/* VS divider */}
                    <div style={{ textAlign:"center", minWidth:52 }}>
                      <div style={{ fontSize:11, color:"#222" }}>@</div>
                      <div style={{ marginTop:4, height:3, borderRadius:2, opacity:0.5,
                        background:`linear-gradient(90deg,${am.color},${hm.color})` }} />
                      <div style={{ fontSize:10, color:"#333", marginTop:3 }}>
                        {g.prob.away}–{g.prob.home}
                      </div>
                    </div>

                    {/* Home team */}
                    <div style={{ flex:1, display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ position:"relative" }}>
                        <div style={{ width:42, height:42, borderRadius:"50%", background:hm.color,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:11, fontWeight:900, color:"#fff", flexShrink:0,
                          border:g.predicted===g.home
                            ?"2px solid #e8f427":"2px solid rgba(255,255,255,0.1)" }}>
                          {g.home}
                        </div>
                        <div style={{ position:"absolute", bottom:-2, right:-2, fontSize:10,
                          background:hm.color, borderRadius:3, padding:"1px 4px",
                          color:"#fff", fontWeight:900, border:"1px solid #060810" }}>
                          {Math.round(g.homeScore)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:15, fontWeight:700,
                          color:g.predicted===g.home?"#fff":"#555" }}>
                          {g.predicted===g.home &&
                            <span style={{ marginRight:5, color:"#e8f427" }}>◆</span>}
                          {g.home}
                        </div>
                        <div style={{ fontSize:11, color:"#444", marginTop:1 }}>{g.homePitcher}</div>
                        <div style={{ fontSize:10, color:"#333" }}>
                          wOBA {g.home_stats?.wOBA} · FIP {g.home_stats?.fip}
                        </div>
                      </div>
                    </div>

                    {/* Confidence badge */}
                    <div style={{ minWidth:62, textAlign:"center", padding:"8px 10px",
                      borderRadius:4, background:confBg(g.confidence),
                      border:`1px solid ${cc}30`, flexShrink:0 }}>
                      <div style={{ fontSize:20, fontWeight:900, color:cc, lineHeight:1 }}>
                        {g.confPct}%
                      </div>
                      <div style={{ fontSize:10, color:"#555", marginTop:2 }}>
                        {g.confidence.toUpperCase()}
                      </div>
                    </div>
                  </div>

                  {/* Win probability bar */}
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10 }}>
                    <span style={{ fontSize:10, color:"#333", width:30, textAlign:"right" }}>
                      {g.prob.away}%
                    </span>
                    <div style={{ flex:1, height:5, borderRadius:2, overflow:"hidden",
                      background:"rgba(255,255,255,0.05)" }}>
                      <div style={{ display:"flex", height:"100%" }}>
                        <div style={{ width:`${g.prob.away}%`, background:am.color, opacity:0.75 }} />
                        <div style={{ width:`${g.prob.home}%`, background:hm.color, opacity:0.75 }} />
                      </div>
                    </div>
                    <span style={{ fontSize:10, color:"#333", width:30 }}>{g.prob.home}%</span>
                  </div>

                  {/* Vegas odds */}
                  {g.odds ? (
                    <div style={{ display:"flex", alignItems:"center", gap:14, marginTop:8,
                      padding:"6px 10px", background:"rgba(255,255,255,0.02)",
                      borderRadius:3, border:"1px solid rgba(255,255,255,0.05)", flexWrap:"wrap" }}>
                      <span style={{ fontSize:10, color:"#444", letterSpacing:"0.1em" }}>🎰 VEGAS</span>
                      {[
                        { t:g.away, ml:g.odds.awayML },
                        { t:g.home, ml:g.odds.homeML },
                      ].map(o => (
                        <span key={o.t} style={{ fontSize:11, color:"#888" }}>
                          {o.t}:{" "}
                          <span style={{ color:o.ml>0?"#4ade80":"#f96e6e", fontWeight:700 }}>
                            {o.ml>0?"+":""}{o.ml??"—"}
                          </span>
                        </span>
                      ))}
                      {g.odds.total &&
                        <span style={{ fontSize:11, color:"#888" }}>
                          O/U <strong style={{ color:"#fff" }}>{g.odds.total}</strong>
                        </span>}
                      {g.edge && (
                        <span style={{ marginLeft:"auto", fontSize:11, fontWeight:700,
                          padding:"2px 8px", borderRadius:3,
                          background:g.edge.includes("VALUE")?"rgba(74,222,128,0.1)":"rgba(255,255,255,0.04)",
                          color:g.edge.includes("VALUE")?"#4ade80":g.edge.includes("Fade")?"#f96e6e":"#888",
                          border:`1px solid ${g.edge.includes("VALUE")?"rgba(74,222,128,0.3)":"rgba(255,255,255,0.05)"}` }}>
                          {g.edge}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{ marginTop:8, fontSize:10, color:"#2a2a2a" }}>
                      Vegas lines not loaded — add REACT_APP_ODDS_API_KEY to .env
                      (free at the-odds-api.com)
                    </div>
                  )}

                  {/* Sabermetric detail */}
                  <div style={{ display:"flex", gap:16, marginTop:8, flexWrap:"wrap" }}>
                    {[
                      { l:"Hard Hit",    a:g.away_stats?.hardHit,   h:g.home_stats?.hardHit,    fmt:v=>`${v}%`, hi:true  },
                      { l:"K/9",         a:g.away_stats?.k9,        h:g.home_stats?.k9,         fmt:v=>v,       hi:true  },
                      { l:"Bullpen ERA", a:g.away_stats?.bullpenEra, h:g.home_stats?.bullpenEra, fmt:v=>v,       hi:false },
                      { l:"DRS",         a:g.away_stats?.drs,       h:g.home_stats?.drs,        fmt:v=>v,       hi:true  },
                    ].map(m => {
                      const aW = m.hi ? m.a >= m.h : m.a <= m.h;
                      return (
                        <div key={m.l} style={{ fontSize:10 }}>
                          <div style={{ color:"#222", marginBottom:2 }}>{m.l}</div>
                          <span style={{ color:aW?"#4ade80":"#555", fontWeight:aW?700:400 }}>
                            {m.fmt(m.a)}
                          </span>
                          <span style={{ color:"#222" }}> / </span>
                          <span style={{ color:!aW?"#4ade80":"#555", fontWeight:!aW?700:400 }}>
                            {m.fmt(m.h)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>}

      {/* ══════════════════════════════════════════════════════════════════════
          WIN TRACKER TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === "tracker" && (
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"32px 24px" }}>
          <div style={{ fontSize:13, letterSpacing:"0.2em", color:"#e8f427", marginBottom:16 }}>
            📊 WIN TRACKER
          </div>
          <div style={{ color:"#444", fontSize:12, marginBottom:20 }}>
            Mark each game result after it finishes. Results are saved in your browser.
          </div>
          <div style={{ display:"grid", gap:8 }}>
            {games.map(g => {
              const savedKey = `result_${g.id}_${new Date().toISOString().split("T")[0]}`;
              const saved    = localStorage.getItem(savedKey);
              const cc       = confColor(g.confidence);
              return (
                <div key={g.id} style={{ display:"flex", alignItems:"center", gap:14,
                  padding:"10px 14px", background:"rgba(255,255,255,0.025)",
                  border:"1px solid rgba(255,255,255,0.07)", borderRadius:4, flexWrap:"wrap" }}>
                  <span style={{ fontSize:13, color:"#888", minWidth:130 }}>
                    {g.away} @ {g.home}
                  </span>
                  <span style={{ fontSize:13, fontWeight:700, color:"#e8f427" }}>
                    Pick: {g.predicted}
                  </span>
                  <span style={{ fontSize:12, color:cc }}>{g.confPct}% {g.confidence}</span>
                  {saved && (
                    <span style={{ fontSize:12, fontWeight:700, marginLeft:4,
                      color:saved==="W"?"#4ade80":"#f96e6e" }}>
                      {saved==="W" ? "✓ WIN" : "✗ LOSS"}
                    </span>
                  )}
                  {!saved && (
                    <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
                      <button onClick={() => {
                        localStorage.setItem(savedKey, "W");
                        load(false);
                      }} style={{ padding:"4px 14px", fontSize:11,
                        background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.3)",
                        color:"#4ade80", borderRadius:3, cursor:"pointer", fontFamily:"inherit" }}>
                        ✓ WIN
                      </button>
                      <button onClick={() => {
                        localStorage.setItem(savedKey, "L");
                        load(false);
                      }} style={{ padding:"4px 14px", fontSize:11,
                        background:"rgba(249,110,110,0.1)", border:"1px solid rgba(249,110,110,0.3)",
                        color:"#f96e6e", borderRadius:3, cursor:"pointer", fontFamily:"inherit" }}>
                        ✗ LOSS
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PRINT SHEET TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === "print" && (
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"24px" }}>
          <div className="np" style={{ display:"flex", justifyContent:"space-between",
            alignItems:"center", marginBottom:20 }}>
            <div style={{ fontSize:13, letterSpacing:"0.2em", color:"#e8f427" }}>🖨️ PRINT SHEET</div>
            <button onClick={() => window.print()} style={{ padding:"8px 24px", fontSize:12,
              background:"#e8f427", color:"#060810", border:"none", borderRadius:3,
              cursor:"pointer", fontWeight:900, fontFamily:"inherit" }}>
              PRINT / SAVE PDF
            </button>
          </div>

          <div id="ps" style={{ background:"#fff", color:"#000", padding:24,
            borderRadius:4, fontFamily:"Arial,sans-serif" }}>
            <div style={{ borderBottom:"3px solid #000", paddingBottom:12, marginBottom:20,
              display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
              <div>
                <div style={{ fontSize:22, fontWeight:900 }}>⚾ FREE MLB WIN PREDICTOR</div>
                <div style={{ fontSize:12, color:"#555", marginTop:4 }}>Sabermetric Daily Pick Sheet</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:14, fontWeight:700 }}>{today}</div>
                <div style={{ fontSize:9, color:"#777", marginTop:2 }}>
                  wOBA×28% · FIP×25% · HardHit×12% · K/9×10% · Bullpen×10% · DRS×8%
                </div>
                <div style={{ fontSize:9, color:"#999", marginTop:2 }}>{sources.join(" · ")}</div>
              </div>
            </div>

            {/* Confidence legend */}
            <div style={{ display:"flex", gap:12, marginBottom:16, fontSize:10, flexWrap:"wrap" }}>
              {[
                { c:"High",   l:"🟢 High (~78% historical accuracy)"   },
                { c:"Medium", l:"🟡 Medium (~65% historical accuracy)"  },
                { c:"Low",    l:"🔴 Toss-Up (~53% — use caution)"      },
              ].map(x => (
                <div key={x.c} style={{ padding:"3px 8px", borderRadius:3, fontWeight:600,
                  background:x.c==="High"?"#dcfce7":x.c==="Medium"?"#fefce8":"#fef2f2",
                  color:x.c==="High"?"#16a34a":x.c==="Medium"?"#ca8a04":"#dc2626",
                  border:`1px solid ${x.c==="High"?"#86efac":x.c==="Medium"?"#fde047":"#fca5a5"}` }}>
                  {x.l}
                </div>
              ))}
            </div>

            {/* Table */}
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:9, tableLayout:"fixed" }}>
              <thead>
                <tr style={{ background:"#111", color:"#fff" }}>
                  {["Time","Matchup","Pick","Model%","Cal%","Conf","Vegas ML",
                    "wOBA","FIP","Hard Hit","K/9","Bullpen","Result"].map(h => (
                    <th key={h} style={{ padding:"5px 4px", textAlign:"left", fontWeight:700, fontSize:8 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {games.map((g, i) => {
                  const ps = g.predicted===g.home ? g.home_stats : g.away_stats;
                  const os = g.predicted===g.home ? g.away_stats : g.home_stats;
                  const cc = g.confidence==="High"?"#16a34a":g.confidence==="Medium"?"#ca8a04":"#dc2626";
                  const bg = g.confidence==="High"?"#f0fdf4":g.confidence==="Medium"?"#fefce8":"#fff7f7";
                  return (
                    <tr key={g.id} style={{ background:i%2===0?"#fff":"#fafafa",
                      borderBottom:"1px solid #e5e7eb" }}>
                      <td style={{ padding:"5px 4px", color:"#555" }}>{g.gameTime}</td>
                      <td style={{ padding:"5px 4px", fontWeight:600 }}>{g.away} @ {g.home}</td>
                      <td style={{ padding:"5px 4px", fontWeight:900, fontSize:11 }}>{g.predicted}</td>
                      <td style={{ padding:"5px 4px", fontWeight:700, color:cc }}>{g.confPct}%</td>
                      <td style={{ padding:"5px 4px" }}>
                        <span style={{ padding:"2px 5px", borderRadius:3,
                          background:bg, color:cc, fontWeight:700 }}>
                          {g.confidence}
                        </span>
                      </td>
                      <td style={{ padding:"5px 4px", fontWeight:700, color:"#888", fontSize:8 }}>
                        {g.prob.calHome >= g.prob.calAway
                          ? `${g.prob.calHome}% / ${g.prob.calAway}%`
                          : `${g.prob.calAway}% / ${g.prob.calHome}%`}
                      </td>
                      <td style={{ padding:"5px 4px" }}>
                        {g.odds
                          ? `${g.away} ${g.odds.awayML>0?"+":""}${g.odds.awayML} / ${g.home} ${g.odds.homeML>0?"+":""}${g.odds.homeML}`
                          : "—"}
                      </td>
                      <td style={{ padding:"5px 4px" }}>
                        <span style={{ color:"#16a34a", fontWeight:600 }}>{ps?.wOBA}</span>
                        {" / "}
                        <span style={{ color:"#999" }}>{os?.wOBA}</span>
                      </td>
                      <td style={{ padding:"5px 4px" }}>
                        <span style={{ color:"#16a34a", fontWeight:600 }}>{ps?.fip}</span>
                        {" / "}
                        <span style={{ color:"#999" }}>{os?.fip}</span>
                      </td>
                      <td style={{ padding:"5px 4px" }}>
                        <span style={{ color:"#16a34a", fontWeight:600 }}>{ps?.hardHit}%</span>
                        {" / "}
                        <span style={{ color:"#999" }}>{os?.hardHit}%</span>
                      </td>
                      <td style={{ padding:"5px 4px" }}>
                        <span style={{ color:"#16a34a", fontWeight:600 }}>{ps?.k9}</span>
                        {" / "}
                        <span style={{ color:"#999" }}>{os?.k9}</span>
                      </td>
                      <td style={{ padding:"5px 4px" }}>
                        <span style={{ color:"#16a34a", fontWeight:600 }}>{ps?.bullpenEra}</span>
                        {" / "}
                        <span style={{ color:"#999" }}>{os?.bullpenEra}</span>
                      </td>
                      <td style={{ padding:"5px 4px" }}>
                        <div style={{ width:"100%", height:14, border:"1px solid #ccc", borderRadius:2 }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ marginTop:14, paddingTop:10, borderTop:"1px solid #e5e7eb",
              display:"flex", justifyContent:"space-between", fontSize:9, color:"#999" }}>
              <span>Free APIs: MLB Stats API · Baseball Savant · The Odds API · Zero AI charges</span>
              <span>Model% = raw score rank · Cal% = calibrated real-world probability · Green = pick stat</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"20px 24px",
        borderTop:"1px solid rgba(255,255,255,0.04)",
        display:"flex", justifyContent:"space-between", flexWrap:"wrap",
        gap:8, fontSize:10, color:"#2a2a2a" }}>
        <span>MLB Free Predictor · Zero AI charges · All free APIs</span>
        <span>Data: {sources.join(" · ")}</span>
      </div>
    </div>
  );
}
